import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { formatarData } from 'src/app/core/utils/utilities';
import {
  IOpcaoAcao,
  IOpcaoTela
} from 'src/app/core/utils/permissionScreen/permissionScreen.types';
import { validarPermissao } from 'src/app/core/utils/permissionScreen/permissionScreen';
import { CaixaService } from 'src/app/services/CaixaService/caixa.service';
import { EstoqueService } from 'src/app/services/EstoqueService/estoque.service';
import { IVendaEstoquePaginatedItem } from 'src/app/services/EstoqueService/estoque.model';
import { VendaEstoqueService } from 'src/app/services/VendaEstoqueService/venda-estoque.service';
import { SwalAlertService } from 'src/app/services/SwalAlertService/swalAlert.service';
import { AppExportBase64 } from 'src/app/services/ExportBase64/exportBase64.service';
import { ImpressoraService } from 'src/app/services/ImpressoraService/impressora.service';
import { IGetReportBase64 } from 'src/app/services/ReportService/report.model';
import { ModalVendaEstoqueComponent } from '../modal-venda-estoque/modal-venda-estoque.component';

type IOptions = { name: string; value: string | number | boolean | null };

@Component({
  selector: 'app-listagem-caixa-estoque',
  templateUrl: './listagem-caixa-estoque.component.html',
  styleUrls: ['./listagem-caixa-estoque.component.scss']
})
export class ListagemCaixaEstoqueComponent implements OnInit, OnDestroy {
  readonly TIPO_CAIXA_ESTOQUE = 3;

  form = this.fb.group({
    FK_CAIX: [null as number | null],
    DT_INIC: [''],
    DT_FIM: [''],
    FL_CNCL: [null as boolean | null],
    PAGE_NUMBER: [1],
    PAGE_SIZE: [8]
  });

  isLoading = false;
  caixaOpen = false;
  caixaId: number | null = null;
  valorTotalCaixa = 0;
  tableData: Array<any> = [];
  maxPages = 1;

  private readonly destroy$ = new Subject<void>();
  private loadingVendas = false;

  cancelamentoOptions: IOptions[] = [
    { name: 'Todos', value: null },
    { name: 'Ativas', value: false },
    { name: 'Canceladas', value: true }
  ];

  tableColumns = [
    { header: 'Código', key: 'codigo', type: 'text' },
    { header: 'Data', key: 'data', type: 'text' },
    { header: 'Cliente', key: 'cliente', type: 'text' },
    { header: 'Itens', key: 'itens', type: 'text' },
    { header: 'Valor Total', key: 'valorTotal', type: 'text' },
    { header: 'Desconto', key: 'desconto', type: 'text' },
    { header: 'Acréscimo', key: 'acrescimo', type: 'text' },
    { header: 'Valor Final', key: 'valorFinal', type: 'text' },
    { header: 'Operador', key: 'operador', type: 'text' },
    { header: 'Situação', key: 'situacao', type: 'text' },
    {
      header: 'Ações',
      key: 'actions',
      type: 'buttons',
      buttons: [
        {
          label: 'Download Ticket',
          img: 'assets/icon-download.svg',
          action: (item: any) => this.downloadTicket(item),
          condition: (item: any) => !item.FL_CNCL
        },
        {
          label: 'Impressão Ticket',
          img: 'assets/icon-print.svg',
          action: (item: any) => this.imprimirTicket(item),
          condition: (item: any) => !item.FL_CNCL
        },
        {
          label: 'Cancelar',
          img: 'assets/trashcan.png',
          action: (item: any) => this.cancelarVenda(item),
          condition: (item: any) => !item.FL_CNCL && this.viewMenu('caixaEstoque', 'deletar')
        }
      ]
    }
  ];

  constructor(
    private fb: FormBuilder,
    public dialog: MatDialog,
    private caixaService: CaixaService,
    private estoqueService: EstoqueService,
    private vendaEstoqueService: VendaEstoqueService,
    private appExportBase64: AppExportBase64,
    private impressoraService: ImpressoraService,
    public swalAlert: SwalAlertService
  ) {}

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.caixaId) {
          this.loadVendas();
        }
      });

    this.openCaixa();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  viewMenu(menu: IOpcaoTela, acao: IOpcaoAcao): boolean {
    return validarPermissao(menu, acao);
  }

  /** Venda permitida com permissão de Caixa Estoque ou de Higienização (abertura só pela higienização). */
  podeRealizarVenda(): boolean {
    return (
      this.viewMenu('caixaEstoque', 'incluir') ||
      this.viewMenu('caixaHigienizacao', 'incluir')
    );
  }

  openCaixa(): void {
    this.isLoading = true;
    this.caixaService.GetCaixaAtual(this.TIPO_CAIXA_ESTOQUE).subscribe({
      next: res => {
        if (!res?.PK_CAIX) {
          this.caixaOpen = false;
          this.caixaId = null;
          this.tableData = [];
          this.valorTotalCaixa = 0;
          this.isLoading = false;
          return;
        }

        this.caixaId = res.PK_CAIX;
        this.caixaOpen = true;
        this.NU_TIPO_CAIX_check(res.NU_TIPO_CAIX);
        this.form.patchValue({ FK_CAIX: res.PK_CAIX }, { emitEvent: false });
        this.loadVendas();
        this.setValorTotalCaixa();
      },
      error: () => {
        this.caixaOpen = false;
        this.caixaId = null;
        this.isLoading = false;
      }
    });
  }

  private NU_TIPO_CAIX_check(tipo: number | null | undefined): void {
    if (tipo !== null && tipo !== undefined && tipo !== this.TIPO_CAIXA_ESTOQUE) {
      this.swalAlert.showAlert(
        'Atenção!',
        'O caixa aberto não é do tipo Estoque.',
        'warning'
      );
    }
  }

  loadVendas(): void {
    if (!this.caixaId || this.loadingVendas) return;

    this.loadingVendas = true;
    this.isLoading = true;

    const params: any = {
      FK_CAIX: this.caixaId,
      DT_INIC: this.form.controls.DT_INIC.value || undefined,
      DT_FIM: this.form.controls.DT_FIM.value || undefined,
      PAGE_NUMBER: this.form.controls.PAGE_NUMBER.value,
      PAGE_SIZE: this.form.controls.PAGE_SIZE.value
    };

    const flCncl = this.form.controls.FL_CNCL.value;
    if (flCncl !== null && flCncl !== undefined) {
      params.FL_CNCL = flCncl;
    }

    this.estoqueService.GetVendasPaginated(params).subscribe({
      next: response => {
        this.tableData = (response.ITEMS ?? []).map((item: IVendaEstoquePaginatedItem) => ({
          codigo: item.PK_VEND_ESTQ,
          data: formatarData(item.DT_CRIA),
          cliente: item.NM_CLNT ?? '---',
          itens: item.TX_ITENS ?? '---',
          valorTotal: Number(item.VL_TOTL).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          }),
          desconto: Number(item.VL_DSCT ?? 0).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          }),
          acrescimo: Number(item.VL_ACRM ?? 0).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          }),
          valorFinal: Number(item.VL_FINAL).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          }),
          operador: item.NM_FUNC_OPER ?? '---',
          situacao: item.FL_CNCL ? 'Cancelada' : 'Ativa',
          ...item
        }));
        this.maxPages = response.TOTAL_PAGES ?? 1;
        this.loadingVendas = false;
        this.isLoading = false;
      },
      error: () => {
        this.loadingVendas = false;
        this.isLoading = false;
      }
    });
  }

  setValorTotalCaixa(): void {
    if (!this.caixaId) return;
    this.caixaService
      .GetCaixaTotalizadorById({ PK_CAIX: this.caixaId, NU_TIPO_PGMT: 0 })
      .subscribe({
        next: res => {
          this.valorTotalCaixa = res.VL_TOTL_CAIX;
        }
      });
  }

  mudarPagina(page: number): void {
    this.form.controls.PAGE_NUMBER.setValue(page + 1);
  }

  openModalNovaVenda(): void {
    if (!this.caixaOpen || !this.caixaId) {
      this.swalAlert.showAlert(
        'Atenção!',
        'Para vender no estoque, abra o caixa de higienização (serviços). O caixa de estoque abre automaticamente junto com ele.',
        'warning'
      );
      return;
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.maxWidth = '900px';
    dialogConfig.width = 'min(900px, 95vw)';
    dialogConfig.maxHeight = '90vh';
    dialogConfig.disableClose = true;
    dialogConfig.data = { PK_CAIX: this.caixaId };

    const dialogRef = this.dialog.open(ModalVendaEstoqueComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadVendas();
        this.setValorTotalCaixa();
      }
    });
  }

  downloadTicket(item: any): void {
    const pk = Number(item.PK_VEND_ESTQ ?? item.codigo);
    if (!pk) return;

    this.isLoading = true;
    this.vendaEstoqueService.GetReceiptVendaEstoqueById(pk, 0).subscribe({
      next: (response: IGetReportBase64) => {
        this.appExportBase64.downloadPDF(response.ReportBase64, 'recibo-venda-estoque');
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  imprimirTicket(item: any): void {
    const pk = Number(item.PK_VEND_ESTQ ?? item.codigo);
    if (!pk) return;

    this.isLoading = true;
    this.vendaEstoqueService.GetReceiptVendaEstoqueById(pk, 1).subscribe({
      next: (response: IGetReportBase64) => {
        this.isLoading = false;
        this.impressoraService.impressoraTicket(response.ReportBase64, 'text/plain');
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  cancelarVenda(item: any): void {
    this.swalAlert.showAlert(
      'Atenção!',
      'Deseja cancelar esta venda?',
      'warning',
      true,
      true,
      undefined,
      () => {
        const funcionarioId = Number(localStorage.getItem('id') ?? 0);
        this.isLoading = true;
        this.vendaEstoqueService
          .CancelVendaEstoque(item.PK_VEND_ESTQ, { FK_FUNC_OPER: funcionarioId })
          .subscribe({
            next: () => {
              this.isLoading = false;
              this.loadVendas();
              this.setValorTotalCaixa();
            },
            error: () => {
              this.isLoading = false;
            }
          });
      }
    );
  }
}
